import type { PourCard } from '@/lib/apiClient';
import { pourStatusLabel } from './pourShared';
import { csvCell, jobLabel, sortCardsBy, wallDate, wallTime } from './pourLog';

/**
 * The Rock Log: every Rock/Aggregate delivery as a flat, filterable, sortable
 * table with quantity totals and a CSV export. It's the Pour Log's sibling:
 * same layout and filters, but rock is ordered in loads, tons or yards
 * depending on the job, so its totals are per unit rather than one CY figure.
 * Pure functions here so the arithmetic is tested; RockLogTable is only the UI.
 */

// No 'covered': that's a will-call-only status.
export const ROCK_STATUSES = ['pending', 'confirmed', 'poured', 'cancelled'] as const;
/** Cancelled orders were never delivered; one click away like the Pour Log's. */
export const DEFAULT_ROCK_STATUSES: readonly string[] = ['pending', 'confirmed', 'poured'];

const STATUS_ORDER: Record<string, number> = { pending: 1, confirmed: 2, poured: 3, cancelled: 4 };

export interface RockLogFilter {
  search: string;
  statuses: readonly string[];
  /** Inclusive YYYY-MM-DD bounds on the delivery's wall-clock date; '' = open. */
  from: string;
  to: string;
}

export type RockLogSortKey =
  | 'job' | 'deliveryDate' | 'aggregate' | 'qty' | 'supplier' | 'costCode' | 'status' | 'source';

export interface RockLogSort { key: RockLogSortKey; dir: 1 | -1 }

const WORD_NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
  nine: 9, ten: 10, eleven: 11, twelve: 12, half: 0.5,
};

/** Spellings the field uses for the same unit, folded to one key. */
const UNIT_ALIASES: Record<string, string> = {
  load: 'load', loads: 'load', truckload: 'load', truckloads: 'load', truck: 'load', trucks: 'load',
  ton: 'ton', tons: 'ton', tn: 'ton', t: 'ton',
  cy: 'cy', yd: 'cy', yds: 'cy', yard: 'cy', yards: 'cy', 'cubic yard': 'cy', 'cubic yards': 'cy',
};

const UNIT_LABEL: Record<string, [string, string]> = {
  load: ['load', 'loads'], ton: ['ton', 'tons'], cy: ['CY', 'CY'],
};

/**
 * "40 Tons" → { amount: 40, unit: 'ton' }; "One load" → { 1, 'load' };
 * "2.5 cy" → { 2.5, 'cy' }. An unrecognized unit is kept as written
 * (lowercased) so it still totals with itself. Null when there's no amount or
 * no unit, e.g. "TBD" or a bare "30" (30 of what?).
 */
export function parseRockQty(text: string | null | undefined): { amount: number; unit: string } | null {
  const t = (text ?? '').trim().toLowerCase().replace(/,/g, '');
  if (!t) return null;
  const m = /^(\d+(?:\.\d+)?|[a-z]+)\s*(.*)$/.exec(t);
  if (!m) return null;
  const amount = /^\d/.test(m[1]) ? Number(m[1]) : WORD_NUMBERS[m[1]];
  if (amount == null || isNaN(amount)) return null;
  const rawUnit = m[2].replace(/^of\s+/, '').replace(/\.$/, '').trim();
  if (!rawUnit) return null;
  // The unit is the leading word(s): "3 loads of #57" and "40 tons (approx)"
  // still total as loads and tons. Only an unrecognized unit is kept whole.
  const head = /^(cubic yards?|[a-z]+)/.exec(rawUnit)?.[1];
  // A SECOND amount + unit ("2 loads, 40 tons") can't be one figure. Reading
  // only the first would drop the rest from the totals without a word, so the
  // whole text counts as unreadable instead (Codex on #582). A number that
  // isn't an amount ("#57 stone") doesn't trip this, and neither does a note
  // in parentheses ("1 load (one truck)") — that restates the order.
  if (SECOND_QTY.test(rawUnit.slice(head?.length ?? 0).replace(/\([^)]*\)/g, ''))) return null;
  return { amount, unit: (head && UNIT_ALIASES[head]) || rawUnit };
}

const SECOND_QTY = new RegExp(
  '(?:^|[^#\\w.])(?:\\d+(?:\\.\\d+)?|' + Object.keys(WORD_NUMBERS).filter((w) => w.length > 2).join('|') + ')\\s*'
  + '(?:' + Object.keys(UNIT_ALIASES).sort((a, b) => b.length - a.length).join('|') + ')\\b',
);

/** 3 + 'load' → "3 loads"; 1 + 'ton' → "1 ton". */
export function fmtRockQty(amount: number, unit: string): string {
  const [one, many] = UNIT_LABEL[unit] ?? [unit, unit];
  const n = amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${n} ${amount === 1 ? one : many}`;
}

export function filterRockLog(cards: readonly PourCard[], f: RockLogFilter): PourCard[] {
  const q = f.search.trim().toLowerCase();
  return cards.filter((c) => {
    if (c.pourType !== 'rock') return false;
    if (!f.statuses.includes(c.status)) return false;
    if (f.from || f.to) {
      const d = wallDate(c.pourDatetime);
      if (!d) return false;
      if (f.from && d < f.from) return false;
      if (f.to && d > f.to) return false;
    }
    if (q) {
      const hay = [jobLabel(c), c.project?.title, c.element, c.location, c.vendor, c.costCode, c.qtyText];
      if (!hay.some((v) => v != null && v.toLowerCase().includes(q))) return false;
    }
    return true;
  });
}

function sortValue(c: PourCard, key: RockLogSortKey): string | number | null {
  switch (key) {
    case 'job':          return jobLabel(c) || null;
    case 'deliveryDate': return c.pourDatetime ? new Date(c.pourDatetime).getTime() : null;
    case 'aggregate':    return c.element;
    // Unit first, then amount, so "3 loads" and "40 tons" don't interleave.
    case 'qty': {
      const p = parseRockQty(c.qtyText);
      // (numeric localeCompare below orders the amounts, decimals included).
      return p ? `${p.unit} ${p.amount}` : null;
    }
    case 'supplier':     return c.vendor;
    case 'costCode':     return c.costCode;
    case 'status':       return STATUS_ORDER[c.status] ?? 99;
    case 'source':       return c.source === 'manual' ? 'Office entry' : 'Field form';
  }
}

/** Same rules as the Pour Log: blanks last both ways, ties in delivery-date order. */
export function sortRockLog(cards: readonly PourCard[], s: RockLogSort): PourCard[] {
  return sortCardsBy(cards, (c) => sortValue(c, s.key), s.dir);
}

export interface RockLogTotals {
  rows: number;
  /** One entry per unit, largest first: [{ unit: 'ton', amount: 120 }, …]. */
  byUnit: { unit: string; amount: number }[];
  /** Rows whose quantity couldn't be read as amount + unit — left out of
   *  byUnit, and counted so a total can't be silently short. */
  unreadableQty: number;
}

export function rockLogTotals(cards: readonly PourCard[]): RockLogTotals {
  const sums = new Map<string, number>();
  let unreadableQty = 0;
  for (const c of cards) {
    const p = parseRockQty(c.qtyText);
    if (p) sums.set(p.unit, (sums.get(p.unit) ?? 0) + p.amount);
    else unreadableQty += 1;
  }
  const byUnit = [...sums.entries()]
    .map(([unit, amount]) => ({ unit, amount: Math.round(amount * 100) / 100 }))
    .sort((a, b) => b.amount - a.amount || a.unit.localeCompare(b.unit));
  return { rows: cards.length, byUnit, unreadableQty };
}

export function rockLogCsv(cards: readonly PourCard[]): string {
  const header = [
    'Job #', 'Job name', 'Delivery date', 'Delivery time', 'Type of aggregate', 'Delivery location',
    'Qty as written', 'Qty', 'Unit', 'Supplier', 'Cost code', 'Field leader', 'Status', 'Entered by',
  ];
  const rows = cards.map((c) => {
    const p = parseRockQty(c.qtyText);
    return [
      jobLabel(c),
      c.project?.title ?? null,
      wallDate(c.pourDatetime),
      wallTime(c.pourDatetime),
      c.element,
      c.location,
      c.qtyText,
      p?.amount ?? null,
      p ? (UNIT_LABEL[p.unit]?.[1] ?? p.unit) : null,
      c.vendor,
      c.costCode,
      c.superName,
      pourStatusLabel(c.status, c.pourType),
      c.source === 'manual' ? `Office${c.createdBy ? ` (${c.createdBy})` : ''}` : 'Field form',
    ];
  });
  // Same CRLF + UTF-8 BOM as the Pour Log, for Excel on Windows.
  return '﻿' + [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
